import { AfterViewInit, Component, computed, effect, input, OnDestroy, output, signal, untracked, viewChild } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { TerraDraw, TerraDrawPolygonMode, TerraDrawRenderMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';

import { GeoCoordinate, GeofenceKind, GeofenceKindColor, SlimGeofence } from '@geofences/geofence';

import { fromGeoJSONRing, ringsEqual, toGeoJSONRing } from './polygon-utils';

import type { GeoJSONStoreFeatures, HexColor } from 'terra-draw';

type FeatureId = string | number;

export enum TerraDrawMode {
  Polygon = 'polygon',
  Select = 'select',
  Render = 'render'
}

export interface GeofenceCoordinates {
  geofence?: SlimGeofence;
  coordinates: GeoCoordinate[];
}

@Component({
  selector: 'app-google-maps-polygon-editor',
  standalone: true,
  imports: [GoogleMap],
  templateUrl: './google-maps-polygon-editor.html',
  styleUrls: ['./google-maps-polygon-editor.scss']
})
export class GoogleMapsPolygonEditorComponent implements AfterViewInit, OnDestroy {
  private readonly googleMapComponent = viewChild(GoogleMap);

  readonly initialCenter = input<GeoCoordinate>({ latitude: 39.5802, longitude: -105.1386 });
  readonly initialZoom = input<number>(12);
  readonly height = input<string>('400px');
  readonly width = input<string>('100%');
  readonly enableDrawing = input<boolean>(true);
  readonly initialDrawingMode = input<boolean>(false);
  readonly geofences = input<SlimGeofence[] | null>(null);
  readonly selectedGeofence = input<SlimGeofence | null>(null);
  readonly restrictToHubBounds = input<boolean>(false);

  readonly geofenceCoordinatesChange = output<GeofenceCoordinates>();
  readonly selectedGeofenceChange = output<SlimGeofence | null>();
  readonly mapReady = output<any>();

  protected readonly mapOptions = signal<google.maps.MapOptions>({
    disableDefaultUI: false,
    zoom: 12,
    center: new google.maps.LatLng(this.initialCenter().latitude, this.initialCenter().longitude),
    mapTypeControl: false,
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    fullscreenControl: false,
    streetViewControl: false,
    rotateControl: false,
    scaleControl: true,
    clickableIcons: false
  });
  protected readonly isDrawingMode = signal<boolean>(false);
  protected readonly googleMap = signal<google.maps.Map | null>(null);

  protected readonly initialCenterLatLng = computed(
    () => new google.maps.LatLng(this.initialCenter().latitude, this.initialCenter().longitude)
  );

  private terraDraw: TerraDraw | null = null;
  private terraDrawReady = false;
  private hasInitiallyFitBounds = false;
  private readonly featureIdToGeofenceMap = new Map<FeatureId, SlimGeofence>();
  private readonly hubGeofence = computed(() => this.geofences()?.find((g) => g.geofenceKind === GeofenceKind.Hub));

  constructor() {
    effect(() => {
      const geofences = this.geofences();
      const googleMap = this.googleMap();
      untracked(() => {
        if (googleMap && this.terraDraw?.enabled && this.terraDrawReady) {
          this.syncGeofences(geofences ?? []);
        }
      });
    });

    effect(() => {
      const initialCenter = this.initialCenter();
      const googleMap = this.googleMap();
      untracked(() => {
        if (googleMap && initialCenter) {
          googleMap.setCenter(new google.maps.LatLng(initialCenter.latitude, initialCenter.longitude));
        }
      });
    });

    effect(() => {
      const selectedGeofence = this.selectedGeofence();
      const googleMap = this.googleMap();
      untracked(() => {
        if (googleMap && this.terraDraw?.enabled && this.terraDrawReady) {
          this.syncSelectedGeofence(selectedGeofence);
        }
      });
    });

    effect(() => {
      const enableDrawing = this.enableDrawing();
      const googleMap = this.googleMap();
      untracked(() => {
        if (googleMap && this.terraDraw?.enabled && !enableDrawing && this.isDrawingMode()) {
          this.terraDraw!.setMode(TerraDrawMode.Select);
          this.isDrawingMode.set(false);
        }
      });
    });
  }

  ngAfterViewInit(): void {
    const googleMapComponent = this.googleMapComponent();
    if (googleMapComponent?.googleMap) {
      const map = googleMapComponent.googleMap;
      google.maps.event.addListenerOnce(map, 'idle', () => {
        this.initializeMap(map);
      });
    }
  }

  ngOnDestroy(): void {
    if (this.terraDraw?.enabled) {
      this.terraDraw.stop();
    }
    this.terraDraw = null;
  }

  initializeMap(map: google.maps.Map): void {
    this.googleMap.set(map);
    this.mapReady.emit(map);
    this.initializeTerraDraw(map);
  }

  setSelectMode(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.terraDraw?.enabled) return;

    this.isDrawingMode.set(false);
    this.terraDraw.setMode(TerraDrawMode.Select);
  }

  toggleDrawingMode(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.terraDraw?.enabled) return;

    this.isDrawingMode.set(true);
    this.terraDraw.setMode(TerraDrawMode.Polygon);
  }

  private initializeTerraDraw(map: google.maps.Map): void {
    const defaultColor = GeofenceKindColor[GeofenceKind.Hub] as HexColor;
    const kindColor = (feature: GeoJSONStoreFeatures): HexColor => {
      const kind = feature.properties?.['geofenceKind'] as GeofenceKind;
      return (GeofenceKindColor[kind] as HexColor) ?? defaultColor;
    };

    this.terraDraw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({ map, lib: google.maps, coordinatePrecision: 16 }),
      modes: [
        new TerraDrawPolygonMode({
          styles: {
            fillColor: defaultColor,
            fillOpacity: 0.35,
            outlineColor: defaultColor,
            outlineWidth: 2
          }
        }),
        new TerraDrawSelectMode({
          // Geofence deletion is handled by the app, not the map — the Delete key
          // would remove the feature without the parent's geofence list knowing.
          keyEvents: { deselect: 'Escape', delete: null, rotate: null, scale: null },
          flags: {
            [TerraDrawMode.Render]: {
              feature: {
                draggable: false,
                coordinates: {
                  // Object form required — `midpoints: true` only allows click-to-insert, not drag
                  midpoints: { draggable: true },
                  draggable: true,
                  deletable: true
                }
              }
            }
          },
          styles: {
            selectedPolygonColor: kindColor,
            selectedPolygonFillOpacity: 0.5,
            selectedPolygonOutlineColor: kindColor,
            selectedPolygonOutlineWidth: 3
          }
        }),
        new TerraDrawRenderMode({
          modeName: TerraDrawMode.Render,
          styles: {
            polygonFillColor: kindColor,
            polygonFillOpacity: 0.35,
            polygonOutlineColor: kindColor,
            polygonOutlineWidth: 2
          }
        })
      ]
    });

    this.terraDraw.start();

    if (this.enableDrawing() && this.initialDrawingMode()) {
      this.terraDraw.setMode(TerraDrawMode.Polygon);
      this.isDrawingMode.set(true);
    } else {
      this.terraDraw.setMode(TerraDrawMode.Select);
    }

    this.terraDraw.on('ready', () => {
      this.terraDrawReady = true;
      // Sync any geofences that arrived before Terra Draw was fully ready
      const geofences = this.geofences();
      if (geofences?.length) {
        this.syncGeofences(geofences);
      }
    });
    // Coordinate changes are emitted from the 'finish' event only. The 'change' event
    // also fires for property-only updates (e.g. selection sets `selected: true`) and
    // for every provisional update mid-drag — emitting those lets parents echo the
    // geofences input back and rebuild the features, which destroys the active selection.
    this.terraDraw.on('finish', (id) => this.handleFinish(id));
    this.terraDraw.on('select', (id) => this.handleSelect(id));
    this.terraDraw.on('deselect', () => this.selectedGeofenceChange.emit(null));
  }

  private handleFinish(id: FeatureId): void {
    if (!this.terraDraw) return;

    const feature = this.terraDraw.getSnapshotFeature(id);
    if (!feature || feature.geometry.type !== 'Polygon') return;

    const coordinates = fromGeoJSONRing(feature.geometry.coordinates[0]);

    // If this is an existing geofence being edited, emit updated coords but keep the feature
    const existingGeofence = this.featureIdToGeofenceMap.get(id);
    if (existingGeofence) {
      if (this.isWithinHubBounds(coordinates)) {
        this.geofenceCoordinatesChange.emit({ geofence: existingGeofence, coordinates });
      }
      return;
    }

    // New polygon drawn — remove it, parent will pass it back via geofences input
    this.terraDraw.removeFeatures([id]);
    this.isDrawingMode.set(false);
    this.terraDraw.setMode(TerraDrawMode.Select);

    this.geofenceCoordinatesChange.emit({ coordinates });
  }

  private isWithinHubBounds(coordinates: GeoCoordinate[]): boolean {
    if (!this.restrictToHubBounds()) return true;

    const hubCoords = this.hubGeofence()?.coordinates;
    if (!hubCoords?.length) return true;

    return this.validatePolygonWithinBounds(coordinates, hubCoords);
  }

  private handleSelect(id: FeatureId): void {
    const geofence = this.featureIdToGeofenceMap.get(id) ?? null;
    this.selectedGeofenceChange.emit(geofence);
  }

  private syncGeofences(geofences: SlimGeofence[]): void {
    if (!this.terraDraw) return;

    const drawableGeofences = geofences.filter((g) => g.coordinates.length >= 3);

    // Edits made on the map echo back through the geofences input (parents feed the
    // emitted coordinates into the same binding). Rebuilding the features would destroy
    // the active selection — removeFeatures deselects — so when the incoming geofences
    // match what is already rendered, just refresh the bookkeeping and keep the features.
    if (this.geofencesMatchRenderedFeatures(drawableGeofences)) {
      this.refreshGeofenceMap(drawableGeofences);
      return;
    }

    const existingIds = Array.from(this.featureIdToGeofenceMap.keys()).filter(
      (id) => !!this.terraDraw!.getSnapshotFeature(id)
    );
    if (existingIds.length) {
      this.terraDraw.removeFeatures(existingIds);
    }
    this.featureIdToGeofenceMap.clear();

    const features: GeoJSONStoreFeatures[] = drawableGeofences.map((g) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Polygon' as const,
        coordinates: [toGeoJSONRing(g.coordinates)]
      },
      properties: {
        mode: TerraDrawMode.Render,
        geofenceId: g.geofenceId,
        geofenceKind: g.geofenceKind
      }
    }));

    if (features.length) {
      this.terraDraw.addFeatures(features);

      const snapshot = this.terraDraw.getSnapshot();
      for (const feature of snapshot) {
        const geofenceId = feature.properties['geofenceId'] as string;
        const geofence = drawableGeofences.find((g) => g.geofenceId === geofenceId);
        if (geofence) {
          this.featureIdToGeofenceMap.set(feature.id as FeatureId, geofence);
        }
      }
    }

    if (!this.hasInitiallyFitBounds) {
      const hub = geofences.find((g) => g.geofenceKind === GeofenceKind.Hub);
      if (hub?.coordinates.length) {
        this.googleMap()?.fitBounds(this.getCoordinateBounds(hub.coordinates), 50);
        this.hasInitiallyFitBounds = true;
      }
    }

    // Re-apply selection — geofences may have been reloaded after initial selection
    const selected = this.selectedGeofence();
    if (selected) {
      this.syncSelectedGeofence(selected);
    }
  }

  private geofencesMatchRenderedFeatures(geofences: SlimGeofence[]): boolean {
    if (!this.terraDraw || geofences.length !== this.featureIdToGeofenceMap.size) {
      return false;
    }

    const featureIdByGeofenceId = new Map<string, FeatureId>();
    for (const [featureId, geofence] of this.featureIdToGeofenceMap) {
      featureIdByGeofenceId.set(geofence.geofenceId, featureId);
    }

    return geofences.every((geofence) => {
      const featureId = featureIdByGeofenceId.get(geofence.geofenceId);
      if (featureId === undefined) return false;

      const rendered = this.featureIdToGeofenceMap.get(featureId);
      if (rendered?.geofenceKind !== geofence.geofenceKind) return false;

      const feature = this.terraDraw!.getSnapshotFeature(featureId);
      if (!feature || feature.geometry.type !== 'Polygon') return false;

      return ringsEqual(feature.geometry.coordinates[0], toGeoJSONRing(geofence.coordinates));
    });
  }

  private refreshGeofenceMap(geofences: SlimGeofence[]): void {
    for (const [featureId, rendered] of this.featureIdToGeofenceMap) {
      const updated = geofences.find((g) => g.geofenceId === rendered.geofenceId);
      if (updated) {
        this.featureIdToGeofenceMap.set(featureId, updated);
      }
    }
  }

  private syncSelectedGeofence(geofence: SlimGeofence | null): void {
    if (!this.terraDraw) return;

    if (!geofence) return;

    if (this.isDrawingMode()) {
      this.terraDraw.setMode(TerraDrawMode.Select);
      this.isDrawingMode.set(false);
    }

    for (const [featureId, g] of this.featureIdToGeofenceMap) {
      if (g.geofenceId === geofence.geofenceId) {
        this.terraDraw.selectFeature(featureId);
        break;
      }
    }

    if (geofence.coordinates.length) {
      this.googleMap()?.fitBounds(this.getCoordinateBounds(geofence.coordinates), 50);
    }
  }

  private getCoordinateBounds(coordinates: GeoCoordinate[]): google.maps.LatLngBounds {
    const initialCenterLatLng = new google.maps.LatLng(this.initialCenter().latitude, this.initialCenter().longitude);
    const bounds = new google.maps.LatLngBounds(initialCenterLatLng);
    coordinates.forEach((c) => bounds.extend(new google.maps.LatLng(c.latitude, c.longitude)));
    return bounds;
  }

  private validatePolygonWithinBounds(coordinates: GeoCoordinate[], bounds: GeoCoordinate[]): boolean {
    return coordinates.every((point) => this.isPointInPolygon(point, bounds));
  }

  private isPointInPolygon(point: GeoCoordinate, polygon: GeoCoordinate[]): boolean {
    const { longitude: x, latitude: y } = point;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const { longitude: xi, latitude: yi } = polygon[i];
      const { longitude: xj, latitude: yj } = polygon[j];

      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }

    return inside;
  }
}
